<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';

import { useAuthStore, useConfigStore } from '@lib/pp-shop-lib/stores';
import { useAlertStore } from '@lib/pp-shop-lib/stores';
import { Alert } from '@lib/pp-shop-lib/components';
import { emailSchema, ppapiClient } from '@lib/pp-shop-lib/helpers';
import { redirectAction } from '@lib/pp-shop-lib/helpers';

const { t } = useI18n();

const schema = Yup.object().shape({
    username: Yup.string().required(t('auth.validation.usernameRequired')),
    password: Yup.string().required(t('auth.validation.passwordRequired'))
});

const configStore = useConfigStore();
const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();

function queryValue(value) {
    if (Array.isArray(value)) return value[0] || '';
    return typeof value === 'string' ? value : '';
}

const loginInitialValues = computed(() => ({
    username: queryValue(route.query.email).trim(),
    password: ''
}));

const forgotPasswordOpen = ref(false);
const forgotPasswordEmail = ref('');
const forgotPasswordError = ref('');
const forgotPasswordSubmitting = ref(false);

async function onSubmit(values) {
    const authStore = useAuthStore();
    const { username, password } = values;
    let result = await authStore.login(username, password, 'login');
    if (result?.kid) redirectAction(configStore.loginSuccessRedirect, router)
}

function openForgotPassword() {
    forgotPasswordOpen.value = true;
    forgotPasswordError.value = '';
}

function closeForgotPassword() {
    forgotPasswordOpen.value = false;
    forgotPasswordEmail.value = '';
    forgotPasswordError.value = '';
}

async function submitForgotPassword() {
    const email = forgotPasswordEmail.value.trim();

    if (!email) {
        forgotPasswordError.value = 'Bitte E-Mail-Adresse eingeben.';
        return;
    }

    if (!emailSchema.isValidSync(email)) {
        forgotPasswordError.value = 'Bitte gueltige E-Mail-Adresse eingeben.';
        return;
    }

    forgotPasswordSubmitting.value = true;
    forgotPasswordError.value = '';

    try {
        await ppapiClient.post('password/forgot', {
            email,
            app: configStore.app
        });

        alertStore.success('Wenn ein Konto existiert, wurde eine Email zum Zuruecksetzen gesendet.', 'login');
        closeForgotPassword();
    } catch (error) {
        alertStore.error(error, 'login');
    } finally {
        forgotPasswordSubmitting.value = false;
    }
}
</script>

<template>
    <div class="uk-card uk-card-body uk-align-center">
        <h3 class="uk-card-title">{{ configStore.service }} {{ $t('nav.login') }}</h3>
        <Alert scope="login" />
        <div>
            <Form class="uk-form-horizontal uk-margin-large" @submit="onSubmit" :validation-schema="schema"
                :initial-values="loginInitialValues"
                v-slot="{ errors, isSubmitting }">
                <div class="uk-margin">
                    <label class="uk-form-label" for="form-user">{{ $t('auth.fields.username') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" id="form-user" name="username" type="text"
                            :class="{ 'is-invalid': errors.username }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.username }}</div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label" for="form-password">{{ $t('auth.fields.password') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" id="form-password" name="password" type="password"
                            :class="{ 'is-invalid': errors.password }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.password }}</div>
                </div>

                <div class="uk-margin-top login-actions">
                    <button class="uk-button uk-button-default uk-button-small" :disabled="isSubmitting">
                        <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                        {{ $t('nav.login') }}
                    </button>
                </div>
            </Form>

            <div class="forgot-password-block">
                <a href="#" @click.prevent="openForgotPassword">Passwort vergessen?</a>

                <div v-if="forgotPasswordOpen" class="uk-margin-small-top forgot-password-form">
                    <input
                        v-model="forgotPasswordEmail"
                        class="uk-input"
                        type="email"
                        placeholder="E-Mail-Adresse"
                    >
                    <button
                        type="button"
                        class="uk-button uk-button-default uk-button-small"
                        :disabled="forgotPasswordSubmitting"
                        @click="submitForgotPassword"
                    >
                        Absenden
                    </button>
                    <button
                        type="button"
                        class="uk-button uk-button-link uk-button-small"
                        @click="closeForgotPassword"
                        aria-label="Abbrechen"
                    >
                        x
                    </button>
                </div>
                <div v-if="forgotPasswordError" class="uk-form-danger uk-margin-small-top">{{ forgotPasswordError }}</div>
            </div>

            <div class="register-block uk-margin-top">
                <div>Noch kein Kundenkonto? Jetzt registrieren:</div>
                <router-link to="/shop/account/register" class="uk-button uk-button-default uk-button-small uk-margin-small-top">
                    {{ $t('auth.register') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<style scoped>
.login-actions {
    display: flex;
    justify-content: flex-start;
}

.forgot-password-form {
    display: flex;
    align-items: center;
    gap: 8px;
}

.forgot-password-form .uk-input {
    max-width: 260px;
}
</style>
